import {
    ROCK_THROWER, SPEAR_THROWER, CLUB_SWINGER, SLING_THROWER, ARCHER, LONGBOW_ARCHER,
    CROW_TRAINER, BEEHIVE_THROWER, TREBUCHET, CROSSBOW, FIREFLY_NEST, DRUID, LIGHTNING_WIZARD,
    BOOMERANG_THROWER,
    BUILDER,
    SKILLS, UPGRADE_CHAINS, FINGER,
    ELDER_COUNCIL, WALL_HP_BASE, SCALING
} from '../config';
import { EconomyManager } from '../managers/EconomyManager';
import { PrestigeManager } from '../managers/PrestigeManager';
import { gameStateManager } from '../managers/GameStateManager';
import { achievementManager, CATEGORY_DISPLAY_NAMES } from '../managers/AchievementManager';
import { formatNumber } from '../utils/formatNumber';
import { ElderName } from '../types';

interface ButtonDef {
    key: string;
    unitType: string;
    label: string;
    tooltip: string;
    tab: 'buy' | 'upgrade' | 'skills';
    isGlobal?: boolean;
}

const BUY_TOOLTIPS: Record<string, string> = {
    rock_thrower: `Rock Thrower\nLobbing rocks`,
    spear_thrower: `Spear Thrower\nPiercing projectiles`,
    club_swinger: `Club Swinger\nMelee AoE attack\nHits ${CLUB_SWINGER.maxTargets} enemies`,
    archer: `Archer`,
    sling_thrower: `Sling Thrower\nSlows enemies ${Math.round((1 - SLING_THROWER.slowEffect) * 100)}%`,
    builder: `Builder\nRepairs wall over time\n+${BUILDER.baseRepairRate} HP/sec`,
    longbow_archer: `Longbow Archer\nSniper unit`,
    crow_trainer: `Crow Trainer\nHoming crows`,
    beehive_thrower: `Beehive Thrower\nSwarm zone + slow\nArea denial`,
    trebuchet: `Trebuchet\nHeavy AoE damage`,
    crossbow: `Crossbow\nHigh damage bolts\nPiercing`,
    firefly_nest: `Firefly Nest\n${FIREFLY_NEST.fireflyCount} fireflies per volley\nFire AoE damage`,
    druid: `Druid\nPlaces thorny brambles\nSlows & damages enemies`,
    lightning_wizard: `Lightning Wizard\nChain lightning\n${LIGHTNING_WIZARD.chainCount} chains`,
    boomerang_thrower: `Boomerang Thrower\nPiercing arc — hits ${BOOMERANG_THROWER.basePierceCount} enemies out\nthen returns through them again`,
    poison_arrows: `${SKILLS.poison_arrows.name}\n${SKILLS.poison_arrows.description}`,
    rock_rain: `${SKILLS.rock_rain.name}\n${SKILLS.rock_rain.description}`,
    gold_rush: `${SKILLS.gold_rush.name}\n${SKILLS.gold_rush.description}`,
    lightning_strike: `${SKILLS.lightning_strike.name}\n${SKILLS.lightning_strike.description}`,
    wall_crossbow_top: `Wall Crossbow Top\nHigh damage bolts\nPiercing`,
    wall_crossbow_bottom: `Wall Crossbow Bottom\nHigh damage bolts\nPiercing`,
};

const UNIT_STATS: Record<string, { range: number, baseDamage: number, accuracyPenalty?: number }> = {
    rock_thrower: { range: ROCK_THROWER.range, baseDamage: ROCK_THROWER.baseDamage },
    spear_thrower: { range: SPEAR_THROWER.range, baseDamage: SPEAR_THROWER.baseDamage },
    club_swinger: { range: CLUB_SWINGER.range, baseDamage: CLUB_SWINGER.baseDamage },
    archer: { range: ARCHER.range, baseDamage: ARCHER.baseDamage, accuracyPenalty: ARCHER.accuracyPenalty },
    sling_thrower: { range: SLING_THROWER.range, baseDamage: SLING_THROWER.baseDamage },
    longbow_archer: { range: LONGBOW_ARCHER.range, baseDamage: LONGBOW_ARCHER.baseDamage, accuracyPenalty: LONGBOW_ARCHER.accuracyPenalty },
    crow_trainer: { range: CROW_TRAINER.range, baseDamage: CROW_TRAINER.baseDamage },
    beehive_thrower: { range: BEEHIVE_THROWER.range, baseDamage: BEEHIVE_THROWER.baseDamage },
    trebuchet: { range: TREBUCHET.range, baseDamage: TREBUCHET.baseDamage },
    crossbow: { range: CROSSBOW.range, baseDamage: CROSSBOW.baseDamage },
    firefly_nest: { range: FIREFLY_NEST.range, baseDamage: FIREFLY_NEST.baseDamage },
    druid: { range: DRUID.range, baseDamage: DRUID.baseDamage },
    lightning_wizard: { range: LIGHTNING_WIZARD.range, baseDamage: LIGHTNING_WIZARD.baseDamage },
    boomerang_thrower: { range: BOOMERANG_THROWER.range, baseDamage: BOOMERANG_THROWER.baseDamage },
};

function getRangeText(r: number): string {
    if (!r) return '';
    if (r < 400) return 'Close';
    if (r <= 600) return 'Medium';
    return 'Large';
}

function getUnitMissChance(unitKey: string, eco: EconomyManager): number | null {
    const stat = UNIT_STATS[unitKey];
    if (!stat || stat.accuracyPenalty === undefined) return null;

    let penalty = stat.accuracyPenalty;
    const chain = UPGRADE_CHAINS[unitKey];
    if (chain) {
        const progress = eco.getChainProgress(unitKey);
        for (let i = 0; i < progress; i++) {
            const effect = chain[i]?.effect;
            if (effect?.accuracyPenaltySet !== undefined) {
                penalty = effect.accuracyPenaltySet;
            }
        }
    }
    return penalty;
}

function getUnitDamageStats(unitKey: string, eco: EconomyManager, prestige: PrestigeManager): { perHit: number, combined: number, count: number, baseDmg: number } | null {
    const stat = UNIT_STATS[unitKey];
    if (!stat) return null;

    const baseDmg = stat.baseDamage || 0;
    if (baseDmg === 0) return null;

    let bonusDamageMult = 1;
    const chain = UPGRADE_CHAINS[unitKey];
    if (chain) {
        const progress = eco.getChainProgress(unitKey);
        for (let i = 0; i < progress; i++) {
            const effect = chain[i]?.effect;
            if (effect?.damageMultBonus) {
                bonusDamageMult *= (1 + effect.damageMultBonus);
            }
        }
    }
    const b = prestige.getElderBonuses();
    const achBonuses = achievementManager.getBonuses();
    const prestigeMult = 1 + b.damageMult + (b.damageBonusNB || 0);
    const globalDamageMult = prestigeMult * (1 + achBonuses.damage);

    const dmgPerHit = Math.round(baseDmg * bonusDamageMult * globalDamageMult);
    const count = eco.getUnitCount(unitKey);
    const combinedDmg = dmgPerHit * Math.max(1, count);

    return { perHit: dmgPerHit, combined: combinedDmg, count, baseDmg };
}

function getBuilderRepairStats(eco: EconomyManager, prestige: PrestigeManager): { perTick: number, combined: number, count: number, baseRate: number } | null {
    const baseRate = BUILDER.baseRepairRate;
    const count = eco.getUnitCount('builder');

    // Accumulate repairRateMult from chain upgrades (multiplicative)
    let repairRateMult = 1;
    let repairIntervalMult = 1;
    const chain = UPGRADE_CHAINS['builder'];
    if (chain) {
        const progress = eco.getChainProgress('builder');
        for (let i = 0; i < progress; i++) {
            const effect = chain[i]?.effect;
            if (effect?.repairRateMult !== undefined) repairRateMult *= effect.repairRateMult;
            if (effect?.repairIntervalMult !== undefined) repairIntervalMult *= effect.repairIntervalMult;
        }
    }

    // Elder/prestige bonuses
    const b = prestige.getElderBonuses();
    const elderRepairMult = (1 + b.repairRateMult) * (1 + (b.repairBonusNB || 0));

    // Achievement bonus
    const achRepairBonus = achievementManager.getBonuses().builderRepair;

    const perTick = Math.round(baseRate * repairRateMult * (1 + achRepairBonus) * elderRepairMult);
    // Effective HP/sec: perTick * stackCount / (repairInterval * repairIntervalMult / 1000)
    const ticksPerSec = 1000 / (BUILDER.repairInterval * repairIntervalMult);
    const combined = Math.round(perTick * Math.max(1, count) * ticksPerSec);
    const perSecSingle = Math.round(perTick * ticksPerSec);

    return { perTick: perSecSingle, combined, count, baseRate };
}

const UPGRADE_TOOLTIPS: Record<string, string> = {
    wall_upgrade: `Wall\nExponential HP growth`,
    poison_arrows: `${SKILLS.poison_arrows.name}\n+DPS, +Duration`,
    rock_rain: `${SKILLS.rock_rain.name}\n+Damage, -Cooldown`,
    gold_rush: `${SKILLS.gold_rush.name}\n+Multiplier, +Duration`,
    lightning_strike: `${SKILLS.lightning_strike.name}\n+Damage, +Chains`,
};

export interface ButtonCache {
    container: HTMLElement;
    titleEl: HTMLElement;
    countEl: HTMLElement;
    costEl: HTMLElement;
    descEl: HTMLElement;
    dpsEl?: HTMLElement;
    lastAffordable?: boolean;
    lastCostStr?: string;
    lastCountStr?: string;
    lastDescStr?: string;
    lastTitleStr?: string;
    lastDisplay?: string;
    lastTooltipStr?: string;
    lastDpsStr?: string;
}

// ── Custom tooltip singleton ─────────────────────────────────────
const shopTooltip = (() => {
    const el = document.getElementById('shop-tooltip') as HTMLElement | null;
    let hideTimer: number | null = null;

    function show(text: string, x: number, y: number) {
        if (!el || !text) return;
        if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
        // First line = title (bold), rest = body
        const lines = text.split('\n');
        const title = lines[0];
        const body = lines.slice(1).join('\n');
        el.innerHTML = `<strong>${title}</strong>${body ? body : ''}`;
        // Position: prefer right of cursor, flip left if near right edge
        const pad = 14;
        el.style.opacity = '0';
        el.classList.add('visible');
        const rect = el.getBoundingClientRect();
        let left = x + pad;
        if (left + rect.width > window.innerWidth - 8) left = x - rect.width - pad;
        let top = y - 10;
        if (top + rect.height > window.innerHeight - 8) top = window.innerHeight - rect.height - 8;
        el.style.left = `${left}px`;
        el.style.top = `${top}px`;
        el.style.opacity = '';
    }

    function hide() {
        if (!el) return;
        el.classList.remove('visible');
    }

    return { show, hide };
})();

export class DOMUI {
    private onAction: (key: string) => void;
    private buttonCache: Map<string, ButtonCache> = new Map();
    private defs: ButtonDef[] = [];
    private hasLoggedDebug = false;
    public buyMultiplier: number = 1; // 1, 10, 100, or -1 for max

    // Cached Info Stats UI Elements for real-time updates
    private elSessionTime: HTMLElement | null = null;
    private elTotalTime: HTMLElement | null = null;
    private elHighestWave: HTMLElement | null = null;
    private elEnemiesKilled: HTMLElement | null = null;
    private elBossesKilled: HTMLElement | null = null;
    private elTotalGold: HTMLElement | null = null;
    private infoUpdateInterval: number | null = null;

    // Prestige tab elements
    private prestigePane: HTMLElement | null = null;
    private prestigeInitialized: boolean = false;

    // Elder bonuses (set from UIScene each frame)
    elderBonuses: import('../types').ElderBonuses | null = null;

    constructor(onAction: (key: string) => void) {
        this.onAction = onAction;
        this.initTabs();
        this.initBuyMultiplier();
        this.initButtons();
        this.initSettings();
    }
    private formatTime(ms: number): string {
        const totalSeconds = Math.floor(ms / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        const pad = (num: number) => num.toString().padStart(2, '0');

        if (hours > 0) {
            return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
        }
        return `${pad(minutes)}:${pad(seconds)}`;
    }

    private initSettings() {
        // Settings Overlay Elements
        const btnSettings = document.getElementById('btn-settings');
        const btnSettingsClose = document.getElementById('btn-settings-close');
        const settingsOverlay = document.getElementById('settings-overlay');
        const btnReset = document.getElementById('btn-reset-game');

        const cbShowDamage = document.getElementById('setting-show-damage') as HTMLInputElement;
        const cbShowGold = document.getElementById('setting-show-gold') as HTMLInputElement;

        if (cbShowDamage) {
            cbShowDamage.checked = localStorage.getItem('setting_show_damage') !== 'false';
            cbShowDamage.addEventListener('change', (e) => {
                localStorage.setItem('setting_show_damage', (e.target as HTMLInputElement).checked.toString());
            });
        }
        if (cbShowGold) {
            cbShowGold.checked = localStorage.getItem('setting_show_gold') !== 'false';
            cbShowGold.addEventListener('change', (e) => {
                localStorage.setItem('setting_show_gold', (e.target as HTMLInputElement).checked.toString());
            });
        }

        // Info Overlay Elements
        const btnInfo = document.getElementById('btn-info');
        const btnInfoClose = document.getElementById('btn-info-close');
        const infoOverlay = document.getElementById('info-overlay');

        // Stats Elements
        this.elSessionTime = document.getElementById('stat-session-time');
        this.elTotalTime = document.getElementById('stat-total-time');
        this.elHighestWave = document.getElementById('stat-highest-wave');
        this.elEnemiesKilled = document.getElementById('stat-enemies-killed');
        this.elBossesKilled = document.getElementById('stat-bosses-killed');
        this.elTotalGold = document.getElementById('stat-total-gold');

        // Initialize Settings Modal
        if (btnSettings && btnSettingsClose && settingsOverlay) {
            btnSettings.addEventListener('click', () => {
                settingsOverlay.classList.remove('hidden');
            });
            btnSettingsClose.addEventListener('click', () => {
                settingsOverlay.classList.add('hidden');
            });
        }

        // Initialize Achievements Overlay
        const btnAchievements = document.getElementById('btn-achievements');
        const btnAchievementsClose = document.getElementById('btn-achievements-close');
        const achievementsOverlay = document.getElementById('achievements-overlay');

        if (btnAchievements && btnAchievementsClose && achievementsOverlay) {
            btnAchievements.addEventListener('click', () => {
                this.renderAchievementsDOM();
                achievementsOverlay.classList.remove('hidden');
            });
            btnAchievementsClose.addEventListener('click', () => {
                achievementsOverlay.classList.add('hidden');
            });
            document.addEventListener('mousedown', (e) => {
                if (!achievementsOverlay.classList.contains('hidden')) {
                    if (!achievementsOverlay.contains(e.target as Node)) {
                        achievementsOverlay.classList.add('hidden');
                    }
                }
            });
        }

        // Initialize Info Modal
        if (btnInfo && btnInfoClose && infoOverlay) {
            btnInfo.addEventListener('click', () => {
                this.updateInfoStats();
                infoOverlay.classList.remove('hidden');

                // Start live updates (once per second)
                if (this.infoUpdateInterval === null) {
                    this.infoUpdateInterval = window.setInterval(() => {
                        this.updateInfoStats();
                    }, 1000);
                }
            });
            btnInfoClose.addEventListener('click', () => {
                infoOverlay.classList.add('hidden');

                // Stop live updates
                if (this.infoUpdateInterval !== null) {
                    window.clearInterval(this.infoUpdateInterval);
                    this.infoUpdateInterval = null;
                }
            });
        }

        // Action Buttons
        if (btnReset) {
            btnReset.addEventListener('click', () => {
                const confirmed = confirm("Are you sure you want to HARD RESET your game? Extinction is forever.");
                if (confirmed) {
                    this.onAction('reset_game');
                    settingsOverlay?.classList.add('hidden');
                }
            });
        }
    }

    private updateInfoStats() {
        if (this.elSessionTime) this.elSessionTime.innerText = this.formatTime(gameStateManager.sessionPlayTime);
        if (this.elTotalTime) this.elTotalTime.innerText = this.formatTime(gameStateManager.permanent.totalPlayTime);
        if (this.elHighestWave) this.elHighestWave.innerText = gameStateManager.permanent.highestWave.toLocaleString();
        if (this.elEnemiesKilled) this.elEnemiesKilled.innerText = gameStateManager.permanent.totalKills.toLocaleString();
        if (this.elBossesKilled) this.elBossesKilled.innerText = gameStateManager.permanent.totalBossKills.toLocaleString();
        if (this.elTotalGold) this.elTotalGold.innerText = Math.floor(gameStateManager.permanent.totalGoldEarned).toLocaleString();
    }

    private initTabs() {
        const tabs = document.querySelectorAll('.menu-tab');
        const panes = document.querySelectorAll('.tab-pane');

        tabs.forEach(tab => {
            tab.addEventListener('click', () => {
                const target = tab.getAttribute('data-target');

                tabs.forEach(t => t.classList.remove('active'));
                panes.forEach(p => p.classList.remove('active'));

                tab.classList.add('active');
                document.getElementById(target as string)?.classList.add('active');
            });
        });
    }

    private initBuyMultiplier() {
        const buyPane = document.getElementById('tab-buy');
        if (!buyPane) return;

        const bar = document.createElement('div');
        bar.className = 'buy-multiplier-bar';

        const options: { label: string; value: number }[] = [
            { label: '1x', value: 1 },
            { label: '10x', value: 10 },
            { label: '100x', value: 100 },
            { label: 'Max', value: -1 },
        ];

        options.forEach(opt => {
            const btn = document.createElement('div');
            btn.className = 'buy-mult-btn' + (opt.value === 1 ? ' active' : '');
            btn.textContent = opt.label;
            btn.addEventListener('click', () => {
                this.buyMultiplier = opt.value;
                bar.querySelectorAll('.buy-mult-btn').forEach(b => b.classList.remove('active'));
                btn.classList.add('active');
            });
            bar.appendChild(btn);
        });

        buyPane.prepend(bar);
    }

    private initButtons() {
        this.defs = [
            // Buy
            { key: 'buy_rock_thrower', unitType: 'rock_thrower', label: 'Rock', tooltip: BUY_TOOLTIPS.rock_thrower, tab: 'buy' },
            { key: 'buy_builder', unitType: 'builder', label: 'Builder', tooltip: BUY_TOOLTIPS.builder, tab: 'buy' },
            { key: 'buy_archer', unitType: 'archer', label: 'Archer', tooltip: BUY_TOOLTIPS.archer, tab: 'buy' },
            { key: 'buy_club_swinger', unitType: 'club_swinger', label: 'Club Swinger', tooltip: BUY_TOOLTIPS.club_swinger, tab: 'buy' },
            { key: 'buy_sling_thrower', unitType: 'sling_thrower', label: 'Sling', tooltip: BUY_TOOLTIPS.sling_thrower, tab: 'buy' },
            { key: 'buy_spear_thrower', unitType: 'spear_thrower', label: 'Spear', tooltip: BUY_TOOLTIPS.spear_thrower, tab: 'buy' },
            { key: 'buy_longbow_archer', unitType: 'longbow_archer', label: 'Longbow', tooltip: BUY_TOOLTIPS.longbow_archer, tab: 'buy' },
            { key: 'buy_firefly_nest', unitType: 'firefly_nest', label: 'Fireflies', tooltip: BUY_TOOLTIPS.firefly_nest, tab: 'buy' },
            { key: 'buy_crossbow', unitType: 'crossbow', label: 'Crossbow', tooltip: BUY_TOOLTIPS.crossbow, tab: 'buy' },
            { key: 'buy_beehive_thrower', unitType: 'beehive_thrower', label: 'Bees', tooltip: BUY_TOOLTIPS.beehive_thrower, tab: 'buy' },
            { key: 'buy_crow_trainer', unitType: 'crow_trainer', label: 'Crows', tooltip: BUY_TOOLTIPS.crow_trainer, tab: 'buy' },
            { key: 'buy_druid', unitType: 'druid', label: 'Druid', tooltip: BUY_TOOLTIPS.druid, tab: 'buy' },
            { key: 'buy_trebuchet', unitType: 'trebuchet', label: 'Trebuchet', tooltip: BUY_TOOLTIPS.trebuchet, tab: 'buy' },
            { key: 'buy_lightning_wizard', unitType: 'lightning_wizard', label: 'Wizard', tooltip: BUY_TOOLTIPS.lightning_wizard, tab: 'buy' },
            { key: 'buy_boomerang_thrower', unitType: 'boomerang_thrower', label: 'Boomerang', tooltip: BUY_TOOLTIPS.boomerang_thrower, tab: 'buy' },
            { key: 'buy_wall_crossbow_top', unitType: 'wall_crossbow_top', label: 'Top XBow', tooltip: BUY_TOOLTIPS.wall_crossbow_top, tab: 'buy' },
            { key: 'buy_wall_crossbow_bottom', unitType: 'wall_crossbow_bottom', label: 'Bot XBow', tooltip: BUY_TOOLTIPS.wall_crossbow_bottom, tab: 'buy' },

            // Upgrades
            { key: 'upgrade_finger', unitType: 'finger', label: 'Finger', tooltip: '', tab: 'upgrade', isGlobal: true },
            { key: 'upgrade_rock_thrower', unitType: 'rock_thrower', label: 'Rock', tooltip: '', tab: 'upgrade' },
            { key: 'upgrade_builder', unitType: 'builder', label: 'Builder', tooltip: '', tab: 'upgrade' },
            { key: 'upgrade_archer', unitType: 'archer', label: 'Archer', tooltip: '', tab: 'upgrade' },
            { key: 'upgrade_club_swinger', unitType: 'club_swinger', label: 'Club Swinger', tooltip: '', tab: 'upgrade' },
            { key: 'upgrade_sling_thrower', unitType: 'sling_thrower', label: 'Sling', tooltip: '', tab: 'upgrade' },
            { key: 'upgrade_spear_thrower', unitType: 'spear_thrower', label: 'Spear', tooltip: '', tab: 'upgrade' },
            { key: 'upgrade_longbow_archer', unitType: 'longbow_archer', label: 'Longbow', tooltip: '', tab: 'upgrade' },
            { key: 'upgrade_firefly_nest', unitType: 'firefly_nest', label: 'Fireflies', tooltip: '', tab: 'upgrade' },
            { key: 'upgrade_crossbow', unitType: 'crossbow', label: 'Crossbow', tooltip: '', tab: 'upgrade' },
            { key: 'upgrade_wall_crossbow_top', unitType: 'wall_crossbow_top', label: 'W.Xbow↑', tooltip: '', tab: 'upgrade', isGlobal: true },
            { key: 'upgrade_wall_crossbow_bottom', unitType: 'wall_crossbow_bottom', label: 'W.Xbow↓', tooltip: '', tab: 'upgrade', isGlobal: true },
            { key: 'upgrade_beehive_thrower', unitType: 'beehive_thrower', label: 'Bees', tooltip: '', tab: 'upgrade' },
            { key: 'upgrade_crow_trainer', unitType: 'crow_trainer', label: 'Crows', tooltip: '', tab: 'upgrade' },
            { key: 'upgrade_druid', unitType: 'druid', label: 'Druid', tooltip: '', tab: 'upgrade' },
            { key: 'upgrade_trebuchet', unitType: 'trebuchet', label: 'Trebuchet', tooltip: '', tab: 'upgrade' },
            { key: 'upgrade_lightning_wizard', unitType: 'lightning_wizard', label: 'Wizard', tooltip: '', tab: 'upgrade' },
            { key: 'upgrade_boomerang_thrower', unitType: 'boomerang_thrower', label: 'Boomerang', tooltip: '', tab: 'upgrade' },
            { key: 'upgrade_wall_upgrade', unitType: 'wall_upgrade', label: 'Wall', tooltip: UPGRADE_TOOLTIPS.wall_upgrade, tab: 'upgrade', isGlobal: true },
            // click_damage, builder_efficiency removed — handled by chain upgrades

            // Skills temporarily disabled
        ];

        this.defs.forEach(def => {
            const btn = document.createElement('div');
            btn.className = 'ui-btn disabled';

            btn.innerHTML = `
        <div class="ui-btn-left">
          <div class="ui-btn-title">${def.label}</div>
          <div class="ui-btn-dps"></div>
          <div class="ui-btn-desc"></div>
        </div>
        <div class="ui-btn-right">
          <div class="ui-btn-cost"></div>
          <div class="ui-btn-count"></div>
        </div>
      `;

            btn.addEventListener('pointerenter', (e) => {
                if (e.pointerType === 'touch') return; // touch uses long-press instead
                const cache = this.buttonCache.get(def.key);
                const tip = cache?.lastTooltipStr || def.tooltip || '';
                if (tip) shopTooltip.show(tip, e.clientX, e.clientY);
            });
            btn.addEventListener('pointermove', (e) => {
                if (e.pointerType === 'touch') return;
                const cache = this.buttonCache.get(def.key);
                const tip = cache?.lastTooltipStr || def.tooltip || '';
                if (tip) shopTooltip.show(tip, e.clientX, e.clientY);
            });
            btn.addEventListener('pointerleave', (e) => {
                if (e.pointerType === 'touch') return;
                shopTooltip.hide();
            });

            // Long-press tooltip for touch devices
            let longPressTimer: number | null = null;
            let longPressTriggered = false;
            btn.addEventListener('touchstart', (e) => {
                longPressTriggered = false;
                const touch = e.touches[0];
                const tx = touch.clientX;
                const ty = touch.clientY;
                longPressTimer = window.setTimeout(() => {
                    longPressTriggered = true;
                    const cache = this.buttonCache.get(def.key);
                    const tip = cache?.lastTooltipStr || def.tooltip || '';
                    if (tip) shopTooltip.show(tip, tx, ty);
                }, 500);
            });
            btn.addEventListener('touchmove', () => {
                if (longPressTimer) { clearTimeout(longPressTimer); longPressTimer = null; }
            });
            btn.addEventListener('touchend', (e) => {
                if (longPressTimer) { clearTimeout(longPressTimer); longPressTimer = null; }
                if (longPressTriggered) {
                    e.preventDefault(); // suppress the click that follows
                    longPressTriggered = false;
                    window.setTimeout(() => shopTooltip.hide(), 1500);
                }
            });
            btn.addEventListener('touchcancel', () => {
                if (longPressTimer) { clearTimeout(longPressTimer); longPressTimer = null; }
                longPressTriggered = false;
                shopTooltip.hide();
            });

            btn.addEventListener('click', () => {
                if (btn.classList.contains('disabled')) {
                    btn.classList.remove('shake-error');
                    void btn.offsetWidth; // trigger reflow to restart animation
                    btn.classList.add('shake-error');
                } else {
                    this.onAction(def.key);
                }
            });

            btn.addEventListener('animationend', () => {
                btn.classList.remove('shake-error');
            });

            const targetPane = document.getElementById(`tab-${def.tab === 'upgrade' ? 'upgrades' : def.tab}`);
            if (targetPane) {
                targetPane.appendChild(btn);

                // Cache the DOM nodes
                this.buttonCache.set(def.key, {
                    container: btn,
                    titleEl: btn.querySelector('.ui-btn-title') as HTMLElement,
                    countEl: btn.querySelector('.ui-btn-count') as HTMLElement,
                    costEl: btn.querySelector('.ui-btn-cost') as HTMLElement,
                    descEl: btn.querySelector('.ui-btn-desc') as HTMLElement,
                    dpsEl: btn.querySelector('.ui-btn-dps') as HTMLElement,
                    lastTooltipStr: def.tooltip || '', // Initialize lastTooltipStr
                });
            }
        });
    }

    private updateDomNode(el: HTMLElement, cacheKey: keyof ButtonCache, newValue: string, cache: ButtonCache) {
        if (cache[cacheKey] !== newValue) {
            el.textContent = newValue;
            (cache as any)[cacheKey] = newValue;
        }
    }

    private updateDisplay(btn: HTMLElement, newDisplay: string, cache: ButtonCache) {
        if (cache.lastDisplay !== newDisplay) {
            btn.style.display = newDisplay;
            cache.lastDisplay = newDisplay;
        }
    }

    private updateAffordable(btn: HTMLElement, isAffordable: boolean, cache: ButtonCache) {
        if (cache.lastAffordable !== isAffordable) {
            btn.classList.toggle('disabled', !isAffordable);
            cache.lastAffordable = isAffordable;
        }
    }

    public updateButtons(economy: EconomyManager, prestige: PrestigeManager): void {
        if (!economy) return;

        if (!this.hasLoggedDebug) {
            console.log("DOMUI First Render Debug - upgradeLevels:", economy.upgradeLevels);
            console.log("DOMUI First Render Debug - localStorage stickdefence_save:", localStorage.getItem('stickdefence_save'));
            this.hasLoggedDebug = true;
        }

        let foundNextUnownedBuy = false;

        this.defs.forEach(def => {
            const cache = this.buttonCache.get(def.key);
            if (!cache) return;

            const { container: btn, titleEl, countEl, costEl, descEl } = cache;

            let costStr = '';

            // Handle Buy Amount Multipliers (1x, 10x, 100x, Max)
            if (def.tab === 'buy') {
                // Hide prestige-gated units when not unlocked
                if (def.unitType === 'boomerang_thrower' && !prestige.getElderBonuses().unlockBoomerangThrower) {
                    this.updateDisplay(btn, 'none', cache);
                    return;
                }
                if (def.unitType === 'wall_crossbow_top' && !prestige.getElderBonuses().unlockWallCrossbowTop) {
                    this.updateDisplay(btn, 'none', cache);
                    return;
                }
                if (def.unitType === 'wall_crossbow_bottom' && !prestige.getElderBonuses().unlockWallCrossbowBottom) {
                    this.updateDisplay(btn, 'none', cache);
                    return;
                }

                const count = economy.getUnitCount(def.unitType);
                const isSkillType = def.unitType in SKILLS;

                if (isSkillType && count > 0) {
                    this.updateDisplay(btn, 'flex', cache);
                    this.updateAffordable(btn, false, cache);
                    costStr = 'Owned';
                    this.updateDomNode(countEl, 'lastCountStr', '', cache);
                } else if (!isSkillType) {
                    if (count === 0) {
                        if (foundNextUnownedBuy) {
                            this.updateDisplay(btn, 'none', cache);
                            return; // Skip rendering this button entirely
                        } else {
                            foundNextUnownedBuy = true;
                        }
                    }

                    const qty = this.buyMultiplier === -1
                        ? economy.getMaxAffordable(def.unitType)
                        : this.buyMultiplier;
                    const cost = qty <= 1
                        ? economy.getBuyCost(def.unitType)
                        : economy.getBulkBuyCost(def.unitType, qty);
                    const canAfford = qty > 0 && economy.canAfford(cost);

                    this.updateDisplay(btn, 'flex', cache);
                    this.updateAffordable(btn, canAfford, cache);
                    costStr = this.buyMultiplier === -1 && qty === 0
                        ? formatNumber(economy.getBuyCost(def.unitType))
                        : formatNumber(cost);
                    const qtyLabel = this.buyMultiplier !== 1 ? ` (×${qty})` : '';
                    this.updateDomNode(countEl, 'lastCountStr', count > 0 ? `Owned: ${count}${qtyLabel}` : qtyLabel, cache);
                }
            } else if (def.tab === 'upgrade') {
                // Hide prestige-gated wall crossbow upgrades when not unlocked
                if (def.unitType === 'wall_crossbow_top' && !prestige.getElderBonuses().unlockWallCrossbowTop) {
                    this.updateDisplay(btn, 'none', cache);
                    return;
                }
                if (def.unitType === 'wall_crossbow_bottom' && !prestige.getElderBonuses().unlockWallCrossbowBottom) {
                    this.updateDisplay(btn, 'none', cache);
                    return;
                }
                if (def.unitType === 'boomerang_thrower' && !prestige.getElderBonuses().unlockBoomerangThrower) {
                    this.updateDisplay(btn, 'none', cache);
                    return;
                }

                const count = economy.getUnitCount(def.unitType);
                const hasUnit = def.isGlobal || count > 0;

                if (!hasUnit) {
                    this.updateDisplay(btn, 'none', cache);
                    return;
                }

                const isChainUnit = def.unitType in UPGRADE_CHAINS;
                if (isChainUnit) {
                    if (economy.isChainComplete(def.unitType)) {
                        this.updateDisplay(btn, 'flex', cache);
                        this.updateAffordable(btn, false, cache);
                        costStr = 'MAX';
                        this.updateDomNode(descEl, 'lastDescStr', '', cache);
                        this.updateDomNode(countEl, 'lastCountStr', '', cache);
                    } else {
                        const nextUpgrade = economy.getNextChainUpgrade(def.unitType);
                        if (nextUpgrade) {
                            const chainCost = economy.getChainUpgradeCost(def.unitType);
                            const affordable = economy.canAfford(chainCost);
                            const progress = economy.getChainProgress(def.unitType);
                            const total = economy.getChainLength(def.unitType);

                            this.updateDisplay(btn, 'flex', cache);
                            this.updateAffordable(btn, affordable, cache);

                            this.updateDomNode(titleEl, 'lastTitleStr', def.label, cache);
                            this.updateDomNode(descEl, 'lastDescStr', nextUpgrade.description, cache);
                            costStr = formatNumber(chainCost);
                            this.updateDomNode(countEl, 'lastCountStr', progress > 0 ? `${progress}/${total}` : 'New', cache);
                        }
                    }
                } else {
                    const level = economy.getUpgradeLevel(def.unitType);
                    const cost = economy.getUpgradeCost(def.unitType);
                    const affordable = economy.canAfford(cost);

                    this.updateDisplay(btn, 'flex', cache);
                    this.updateAffordable(btn, affordable, cache);
                    costStr = formatNumber(cost);
                    this.updateDomNode(countEl, 'lastCountStr', level > 0 ? `Lv ${level}` : 'New', cache);
                }

            } else if (def.tab === 'skills') {
                const isOwned = economy.getUnitCount(def.unitType) > 0;
                const isBuyBtn = def.key.startsWith('buy_');
                const isUpgradeBtn = def.key.startsWith('upgrade_');

                if ((isBuyBtn && isOwned) || (isUpgradeBtn && !isOwned)) {
                    this.updateDisplay(btn, 'none', cache);
                    return;
                }

                const cost = isBuyBtn ? economy.getBuyCost(def.unitType) : economy.getUpgradeCost(def.unitType);
                const affordable = economy.canAfford(cost);

                this.updateDisplay(btn, 'flex', cache);
                this.updateAffordable(btn, affordable, cache);
                costStr = formatNumber(cost);

                if (!isBuyBtn) {
                    const level = economy.getUpgradeLevel(def.unitType);
                    this.updateDomNode(countEl, 'lastCountStr', level > 0 ? `Lv ${level}` : 'New', cache);
                } else {
                    this.updateDomNode(countEl, 'lastCountStr', '', cache);
                }
            }

            // Apply cost string if changed
            if (costStr !== cache.lastCostStr) {
                cache.costEl.innerText = costStr;
                cache.lastCostStr = costStr;
            }

            // Dynamic Tooltips Update
            let dynamicTooltip = def.tooltip;
            let dpsStr = '';

            if (def.tab === 'buy') {
                const count = economy.getUnitCount(def.unitType);
                if (count === 0 && !(def.unitType in SKILLS)) {
                    dynamicTooltip = `???\nUnlock this unit to reveal its stats.\nIt's a mystery!`;
                } else {
                    const stat = UNIT_STATS[def.unitType];
                    if (stat !== undefined) {
                        dynamicTooltip += `\nRange: ${getRangeText(stat.range)}`;

                        const dmgInfos = getUnitDamageStats(def.unitType, economy, prestige);
                        if (dmgInfos) {
                            let statsStr = `\nDamage: ${dmgInfos.perHit}`;
                            if (dmgInfos.count > 0 || dmgInfos.perHit > dmgInfos.baseDmg) {
                                statsStr += `\nTotal damage: ${dmgInfos.combined}`;
                            }
                            dynamicTooltip += statsStr;

                            // Add damage formatting directly inside the button layout if owned
                            if (dmgInfos.count > 0) {
                                dpsStr = `Dmg: ${formatNumber(dmgInfos.perHit)} | Total dmg: ${formatNumber(dmgInfos.combined)}`;
                            }
                        }

                        const missChance = getUnitMissChance(def.unitType, economy);
                        if (missChance !== null) {
                            dynamicTooltip += `\n${Math.round(missChance * 100)}% miss at max range`;
                        }
                    }

                    // Builder: show heal per second
                    if (def.unitType === 'builder') {
                        const repairInfos = getBuilderRepairStats(economy, prestige);
                        if (repairInfos) {
                            dynamicTooltip += `\nHeal: ${repairInfos.perTick} HP/s`;
                            if (repairInfos.count > 0) {
                                dpsStr = `Heal: ${formatNumber(repairInfos.perTick)} HP/s`;
                            }
                        }
                    }
                }
            }

            // Wall upgrade: show current and next HP
            if (def.unitType === 'wall_upgrade') {
                const wallLevel = economy.getUpgradeLevel('wall_upgrade');
                const elderWallHP = prestige.getElderBonuses().wallHPMult;
                const elderWallHPNB = prestige.getElderBonuses().wallHPBonusNB;
                const elderMult = 1 + elderWallHP + elderWallHPNB;
                const currentHP = Math.round(WALL_HP_BASE * Math.pow(SCALING.wallHpGrowth, wallLevel) * elderMult);
                const nextHP = Math.round(WALL_HP_BASE * Math.pow(SCALING.wallHpGrowth, wallLevel + 1) * elderMult);
                dynamicTooltip = `Wall HP Upgrade\nCurrent: ${formatNumber(currentHP)} HP\nNext level: ${formatNumber(nextHP)} HP (+${Math.round((SCALING.wallHpGrowth - 1) * 100)}% per level)`;
            }

            if (cache.lastTooltipStr !== dynamicTooltip) {
                cache.lastTooltipStr = dynamicTooltip;
            }

            if (cache.dpsEl && cache.lastDpsStr !== dpsStr) {
                cache.dpsEl.innerText = dpsStr;
                cache.lastDpsStr = dpsStr;
            }
        });
    }

    // ---- Prestige Tab ----

    private initPrestigeTab(): void {
        this.prestigePane = document.getElementById('tab-prestige');
        if (!this.prestigePane) return;
        this.prestigeInitialized = true;

        // Build static structure
        this.prestigePane.innerHTML = `
            <div class="prestige-panel">
                <div class="prestige-crystal-section">
                    <div class="prestige-crystal-orb">
                        <div class="prestige-crystal-orb-ring"></div>
                        <div class="prestige-crystal-orb-ring"></div>
                        <div class="prestige-crystal-orb-inner">
                            <span class="prestige-crystal-value" id="prestige-legacy">0</span>
                            <span class="prestige-crystal-sublabel">Legacy</span>
                        </div>
                    </div>
                    <div class="prestige-count-badge">
                        <span id="prestige-count">0</span> Prestiges
                    </div>
                </div>

                <div class="prestige-run-info">
                    <div class="prestige-run-info-row">
                        <span class="prestige-run-label">Gain if ascend</span>
                        <span class="prestige-run-reward" id="prestige-reward">+0</span>
                    </div>
                    <div class="prestige-run-desc" id="prestige-desc">Earn more gold to unlock prestige</div>
                </div>

                <div class="prestige-invoke-btn disabled" id="btn-prestige">
                    <span class="prestige-invoke-label">Invoke Ancestry</span>
                    <span class="prestige-invoke-sublabel">Forfeit this run · Gain Legacy</span>
                </div>

                <div class="prestige-bonuses" id="prestige-bonuses" style="display:none"></div>

                <div class="prestige-tree-link" id="btn-open-heritage">
                    <span class="prestige-tree-icon">🌀</span>
                    <div class="prestige-tree-text">
                        <div class="prestige-tree-title">Heritage Tree</div>
                        <div class="prestige-tree-sub">Spend Legacy Points</div>
                    </div>
                    <span class="prestige-tree-arrow">›</span>
                </div>
            </div>
        `;

        // Prestige button click handler
        const btnPrestige = document.getElementById('btn-prestige');
        if (btnPrestige) {
            btnPrestige.addEventListener('click', () => {
                if (!btnPrestige.classList.contains('disabled')) {
                    this.showPrestigeConfirmModal();
                }
            });
        }

        // Open Heritage Tree click handler
        const btnOpenHeritage = document.getElementById('btn-open-heritage');
        if (btnOpenHeritage) {
            btnOpenHeritage.addEventListener('click', () => {
                this.onAction('open_prestige_tree');
            });
        }
    }

    public updatePrestigeTab(prestigeManager: PrestigeManager, currentWave: number, goldEarnedThisRun: number): void {
        if (!this.prestigeInitialized) {
            this.initPrestigeTab();
        }
        if (!this.prestigePane) return;

        // Update legacy display
        const legacyEl = document.getElementById('prestige-legacy');
        const countEl = document.getElementById('prestige-count');
        if (legacyEl) legacyEl.textContent = String(prestigeManager.getAvailableLegacy());
        if (countEl) countEl.textContent = String(prestigeManager.getPrestigeCount());

        // Update prestige button
        const btnPrestige = document.getElementById('btn-prestige');
        const descEl = document.getElementById('prestige-desc');
        const rewardEl = document.getElementById('prestige-reward');
        const projectedLegacy = prestigeManager.getProjectedLegacy(goldEarnedThisRun);
        const canPrestige = prestigeManager.canPrestige(goldEarnedThisRun);

        if (btnPrestige) {
            btnPrestige.classList.toggle('disabled', !canPrestige);
        }
        if (descEl) {
            descEl.textContent = projectedLegacy > 0
                ? 'Reset run · Keep all Legacy points'
                : 'Reset run · No legacy yet (earn more gold)';
        }
        if (rewardEl) {
            rewardEl.textContent = `+${projectedLegacy} Legacy`;
        }

        // Update active bonuses summary
        const bonusesEl = document.getElementById('prestige-bonuses');
        if (bonusesEl) {
            const b = prestigeManager.getElderBonuses();
            const lines: string[] = [];
            // Combine flat % bonuses from different elders for a cleaner UI
            const totalDmg = Math.round((b.damageMult + b.damageBonusNB) * 100);
            if (totalDmg > 0) lines.push(`<span class="bonus-stat">+${totalDmg}% Damage</span>`);

            const totalGold = Math.round((b.goldMult + b.goldBonusNB) * 100);
            if (totalGold > 0) lines.push(`<span class="bonus-stat gold">+${totalGold}% Gold</span>`);

            const totalRepair = Math.round((b.repairRateMult + b.repairBonusNB) * 100);
            if (totalRepair > 0) lines.push(`<span class="bonus-stat">+${totalRepair}% Repair</span>`);

            const totalWall = Math.round((b.wallHPMult + b.wallHPBonusNB) * 100);
            if (totalWall > 0) lines.push(`<span class="bonus-stat">+${totalWall}% Wall HP</span>`);

            if (b.autoClickInterval > 0) lines.push(`<span class="bonus-stat">Auto ${Math.round(1000 / b.autoClickInterval)}/s</span>`);
            if (b.unlockWallCrossbowTop) lines.push(`<span class="bonus-stat">Top Turret</span>`);
            if (b.unlockWallCrossbowBottom) lines.push(`<span class="bonus-stat">Bot Turret</span>`);
            if (b.unlockBoomerangThrower) lines.push(`<span class="bonus-stat">Boomerang Unit</span>`);

            // Other specific non-shared bonuses
            if (b.attackSpeedMult > 0) lines.push(`<span class="bonus-stat">+${Math.round(b.attackSpeedMult * 100)}% Atk Speed</span>`);
            if (b.buyCostMult < 1) lines.push(`<span class="bonus-stat gold">-${Math.round((1 - b.buyCostMult) * 100)}% Buy Cost</span>`);
            if (b.chainCostMult < 1) lines.push(`<span class="bonus-stat gold">-${Math.round((1 - b.chainCostMult) * 100)}% Chain Cost</span>`);
            if (b.thornyWallPercent > 0) lines.push(`<span class="bonus-stat">${Math.round(b.thornyWallPercent * 100)}% Thorny</span>`);
            if (b.startWave > 0) lines.push(`<span class="bonus-stat">Start W${b.startWave}</span>`);
            if (b.startWallLevel > 0) lines.push(`<span class="bonus-stat">Wall Lv${b.startWallLevel}</span>`);
            if (b.bossGoldMultOverride > 0) lines.push(`<span class="bonus-stat gold">${b.bossGoldMultOverride}x Boss Gold</span>`);

            if (lines.length > 0) {
                bonusesEl.innerHTML = `<div class="bonus-label">Active Bonuses</div><div class="bonus-list">${lines.join('')}</div>`;
                bonusesEl.style.display = '';
            } else {
                bonusesEl.style.display = 'none';
            }
        }
    }

    private renderAchievementsDOM() {
        const container = document.getElementById('achievements-list');
        const summaryContainer = document.getElementById('achievements-summary');
        if (!container || !summaryContainer) return;

        const categories = achievementManager.getCategories();
        let html = '';

        let totalUnlocked = 0;
        let totalAchievements = 0;
        let bonusDamage = 0;
        let bonusGold = 0;
        let bonusWall = 0;
        let bonusClick = 0;
        let bonusRepair = 0;

        for (const cat of categories) {
            const achievements = achievementManager.getAchievementsByCategory(cat);
            if (achievements.length === 0) continue;

            const unlocked = achievements.filter(a => a.unlocked).length;
            const total = achievements.length;
            const percent = Math.floor((unlocked / total) * 100);

            totalUnlocked += unlocked;
            totalAchievements += total;

            html += `
                <div class="achievement-category">
                    <div class="achievement-cat-header">
                        <div class="achievement-cat-title">${CATEGORY_DISPLAY_NAMES[cat] || cat}</div>
                        <div class="achievement-cat-progress-text">${unlocked} / ${total}</div>
                    </div>
                    <div class="achievement-progress-bar-bg">
                        <div class="achievement-progress-bar-fill" style="width: ${percent}%"></div>
                    </div>
                    <div class="achievement-grid">
            `;

            for (const ach of achievements) {
                const isLocked = !ach.unlocked;

                if (!isLocked) {
                    if (ach.bonusType === 'damage') bonusDamage += ach.bonusPercent;
                    else if (ach.bonusType === 'gold') bonusGold += ach.bonusPercent;
                    else if (ach.bonusType === 'wallHP') bonusWall += ach.bonusPercent;
                    else if (ach.bonusType === 'clickDamage') bonusClick += ach.bonusPercent;
                    else if (ach.bonusType === 'builderRepair') bonusRepair += ach.bonusPercent;
                }

                // Format bonus
                const isGold = ach.bonusType === 'gold';
                const isWall = ach.bonusType === 'wallHP';
                const isClick = ach.bonusType === 'clickDamage';
                const isBuilder = ach.bonusType === 'builderRepair';
                const bonusColor = isGold ? '#ffd700' : isWall ? '#88ffff' : isClick ? '#ffaaaa' : isBuilder ? '#88ff88' : '#ffaa44';

                let bonusLabel = 'DAMAGE';
                if (isGold) bonusLabel = 'GOLD';
                if (isWall) bonusLabel = 'WALL HP';
                if (isClick) bonusLabel = 'CLICK';
                if (isBuilder) bonusLabel = 'REPAIR';

                html += `
                    <div class="achievement-card ${isLocked ? 'locked' : ''}">
                        <div class="achievement-icon">${ach.icon}</div>
                        <div class="achievement-details">
                            <div class="achievement-name">${ach.name}</div>
                            <div class="achievement-desc">${isLocked ? '???' : ach.description}</div>
                        </div>
                        <div class="achievement-bonus">
                            <div class="achievement-bonus-value" style="color: ${bonusColor}">+${ach.bonusPercent}%</div>
                            <div class="achievement-bonus-label" style="color: ${bonusColor}">${bonusLabel}</div>
                        </div>
                    </div>
                `;
            }

            html += `
                    </div>
                </div>
            `;
        }

        container.innerHTML = html;

        // Render Summary
        let summaryHtml = `
            <div class="achievement-summary-item">
                <div class="achievement-summary-value">${totalUnlocked} / ${totalAchievements}</div>
                <div class="achievement-summary-label">Unlocked</div>
            </div>
        `;

        const fmt = (val: number) => parseFloat(val.toFixed(2));

        if (bonusDamage > 0) summaryHtml += `<div class="achievement-summary-item"><div class="achievement-summary-value" style="color: #ffaa44">+${fmt(bonusDamage)}%</div><div class="achievement-summary-label">Damage</div></div>`;
        if (bonusGold > 0) summaryHtml += `<div class="achievement-summary-item"><div class="achievement-summary-value" style="color: #ffd700">+${fmt(bonusGold)}%</div><div class="achievement-summary-label">Gold</div></div>`;
        if (bonusWall > 0) summaryHtml += `<div class="achievement-summary-item"><div class="achievement-summary-value" style="color: #88ffff">+${fmt(bonusWall)}%</div><div class="achievement-summary-label">Wall HP</div></div>`;
        if (bonusClick > 0) summaryHtml += `<div class="achievement-summary-item"><div class="achievement-summary-value" style="color: #ffaaaa">+${fmt(bonusClick)}%</div><div class="achievement-summary-label">Click Dmg</div></div>`;
        if (bonusRepair > 0) summaryHtml += `<div class="achievement-summary-item"><div class="achievement-summary-value" style="color: #88ff88">+${fmt(bonusRepair)}%</div><div class="achievement-summary-label">Repair</div></div>`;

        summaryContainer.innerHTML = summaryHtml;
    }



    private showPrestigeConfirmModal(): void {
        // Remove existing modal if any
        document.getElementById('prestige-modal')?.remove();

        const legacyEl = document.getElementById('prestige-reward');
        const legacyText = legacyEl?.textContent || '+0 Legacy';

        const modal = document.createElement('div');
        modal.id = 'prestige-modal';
        modal.className = 'ptm-overlay';
        modal.innerHTML = `
            <div class="ptm-inner">
                <canvas class="ptm-canvas"></canvas>
                <div class="ptm-sigil">☩</div>
                <h2 class="ptm-title">The Ancestors Call</h2>
                <p class="ptm-body">
                    Your lineage echoes through the ages.<br>
                    All units, upgrades, and gold are forfeit.<br>
                    Elder Council upgrades are eternal.
                </p>
                <div class="ptm-reward">
                    <div class="ptm-reward-label">Legacy Gained</div>
                    <div class="ptm-reward-value">${legacyText}</div>
                </div>
                <div class="ptm-actions">
                    <button class="ptm-btn ptm-cancel" id="prestige-cancel">Remain</button>
                    <button class="ptm-btn ptm-confirm" id="prestige-confirm">
                        Transcend
                        <span class="ptm-confirm-shine"></span>
                    </button>
                </div>
            </div>
        `;

        document.body.appendChild(modal);

        // Animate in
        requestAnimationFrame(() => {
            modal.classList.add('visible');
            // Start particle burst on the canvas
            const canvas = modal.querySelector('.ptm-canvas') as HTMLCanvasElement;
            if (canvas) this.animateModalParticles(canvas);
        });

        document.getElementById('prestige-cancel')?.addEventListener('click', () => {
            modal.classList.remove('visible');
            setTimeout(() => modal.remove(), 380);
        });

        document.getElementById('prestige-confirm')?.addEventListener('click', () => {
            modal.classList.remove('visible');
            setTimeout(() => {
                modal.remove();
                this.onAction('prestige');
            }, 380);
        });
    }

    private animateModalParticles(canvas: HTMLCanvasElement): void {
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        canvas.width = canvas.offsetWidth || 370;
        canvas.height = canvas.offsetHeight || 300;

        const cx = canvas.width / 2;
        const cy = canvas.height * 0.28; // near the sigil

        const particles = Array.from({ length: 48 }, () => {
            const angle = Math.random() * Math.PI * 2;
            const speed = 1.2 + Math.random() * 3.2;
            return {
                x: cx, y: cy,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed - 1.5,
                life: 1.0,
                decay: 0.008 + Math.random() * 0.006,
                r: 1.5 + Math.random() * 2.5,
                hue: 35 + Math.random() * 28,
                lightness: 55 + Math.random() * 25,
            };
        });

        const loop = () => {
            const alive = particles.filter(p => p.life > 0);
            if (alive.length === 0) {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                return;
            }

            ctx.clearRect(0, 0, canvas.width, canvas.height);

            for (const p of particles) {
                if (p.life <= 0) continue;
                p.x += p.vx;
                p.y += p.vy;
                p.vy += 0.07; // gentle gravity
                p.vx *= 0.99;
                p.life -= p.decay;

                ctx.save();
                ctx.globalAlpha = Math.max(0, p.life * 0.85);
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.r * p.life, 0, Math.PI * 2);
                ctx.fillStyle = `hsl(${p.hue}, 90%, ${p.lightness}%)`;
                ctx.fill();
                ctx.restore();
            }
            requestAnimationFrame(loop);
        };
        requestAnimationFrame(loop);
    }
}
